import React, {useMemo} from 'react';
import {Alert, FlexColumn, FlexRow, Modal, Text} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {ExpenseRow} from '../ExpenseTypes';
import {createToast} from '../../../forecast-app/shared/components/toasts/another-toast/toaster';
import BulkDeleteExpenseItemsMutation from '../../../mutations/bulk_delete_expense_items_mutation';
import Util from '../../../forecast-app/shared/util/util';
import {partitionArray} from '../../../forecast-app/shared/util/array-utils';

type ExpenseManagementModalProps = {
	selectedRows: ExpenseRow[];
	companyId: string;
	closeModal: () => void;
	onSuccessfulDelete: () => void;
};

const DeleteExpensesModal = ({
	selectedRows,
	companyId,
	closeModal,
	onSuccessfulDelete,
}: ExpenseManagementModalProps): JSX.Element => {
	const {formatMessage} = useIntl();

	const [selectedLockedRows, selectedUnlockedRows] = useMemo(
		() => partitionArray(selectedRows, item => item.locked),
		[selectedRows]
	);

	const numLockedExpenses = selectedLockedRows.length;
	const numTotalExpenses = selectedRows.length;

	const onDelete = () => {
		closeModal();
		const onSuccess = () => {
			onSuccessfulDelete();
			createToast({
				duration: 5000,
				message: formatMessage({
					id:
						selectedUnlockedRows.length > 1
							? 'expense_item.has_been_deleted_bulk'
							: 'expense_item.has_been_deleted',
				}),
			});
		};
		Util.CommitMutation(
			BulkDeleteExpenseItemsMutation,
			{
				ids: selectedUnlockedRows.map(({id}) => id),
				companyId,
			},
			onSuccess
		);
	};

	return (
		<Modal size={Modal.SIZE.SMALL} onCloseModal={closeModal}>
			<Modal.Title>
				<FormattedMessage id="expense_management.modal.delete_expenses" />
			</Modal.Title>
			<Modal.Content>
				<FlexColumn>
					<FlexRow>
						<Text>
							<FormattedMessage id="expense_management.modal.are_you_sure_delete_expenses" />
						</Text>
					</FlexRow>
					{selectedLockedRows.length ? (
						selectedRows.length > 1 ? (
							<FlexRow>
								<Alert
									text={formatMessage(
										{id: 'expense_management.modal.expenses_locked_cannot_be_deleted'},
										{numLockedExpenses, numTotalExpenses}
									)}
									type={Alert.TYPE.INFORMATION}
								/>
							</FlexRow>
						) : (
							<FlexRow>
								<Alert
									text={formatMessage({id: 'expense_management.modal.expense_locked_cannot_be_deleted'})}
									type={Alert.TYPE.INFORMATION}
								/>
							</FlexRow>
						)
					) : null}
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={onDelete}>
					<FormattedMessage id="expense_management.modal.delete_expenses" />
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={closeModal}>
					<FormattedMessage id={'common.cancel'} />
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export default DeleteExpensesModal;
